import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, PlusCircleIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';

// Interfaces (in English)
interface Project {
    id?: string; // Optional for new projects
    name: string;
    clientId: string;
}

interface Client {
    id: string;
    name: string;
}

interface ProjectFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (
        projectData: { name: string; clientId?: string; id?: string },
        newClientName?: string
    ) => Promise<void>;
    initialProjectData?: Project | null;
    clients: Client[];
    loadingClients: boolean;
}

// Component (in English)
export default function ProjectFormModal({
    isOpen,
    onClose,
    onSubmit,
    initialProjectData,
    clients,
    loadingClients,
}: ProjectFormModalProps) {
    const [name, setName] = useState('');
    const [clientId, setClientId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showNewClientForm, setShowNewClientForm] = useState(false);
    const [newClientName, setNewClientName] = useState('');

    const isEditing = Boolean(initialProjectData && initialProjectData.id);
    const modalTitle = isEditing ? 'Editar Projeto' : 'Adicionar Novo Projeto';
    const submitButtonText = isEditing ? 'Salvar Alterações' : 'Adicionar Projeto';

    useEffect(() => {
        if (isOpen) {
            if (initialProjectData) {
                setName(initialProjectData.name || '');
                setClientId(initialProjectData.clientId || '');
                setShowNewClientForm(false);
                setNewClientName('');
            } else {
                setName('');
                setClientId('');
                setShowNewClientForm(false);
                setNewClientName('');
            }
            setError(null);
        }
    }, [initialProjectData, isOpen]);

    const handleToggleClientForm = () => {
        setShowNewClientForm(!showNewClientForm);
        setError(null);
        if (showNewClientForm) {
            setNewClientName('');
        } else {
            setClientId('');
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null);

        if (!name.trim()) {
            setError('O nome do projeto é obrigatório.');
            return;
        }

        setIsSubmitting(true);
        try {
            if (showNewClientForm && !isEditing) {
                if (!newClientName.trim()) {
                    setError('O nome do novo cliente é obrigatório.');
                    setIsSubmitting(false);
                    return;
                }
                await onSubmit({ name: name.trim() }, newClientName.trim());
            } else {
                if (!clientId && !isEditing) {
                    setError('O cliente é obrigatório.');
                    setIsSubmitting(false);
                    return;
                }
                if (isEditing && !clientId && initialProjectData?.clientId) {
                    setClientId(initialProjectData.clientId);
                }
                if (!clientId && isEditing && !initialProjectData?.clientId) {
                    setError('Cliente original não encontrado para edição.');
                    setIsSubmitting(false);
                    return;
                }

                const projectPayload: { name: string; clientId: string; id?: string } = {
                    name: name.trim(),
                    clientId: isEditing ? initialProjectData!.clientId : clientId,
                };

                if (isEditing && initialProjectData?.id) {
                    projectPayload.id = initialProjectData.id;
                }
                await onSubmit(projectPayload);
            }
        } catch (submissionError: any) {
            console.error("Error submitting project from modal:", submissionError);
            setError(submissionError.message || "Falha ao salvar projeto. Tente novamente.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                    <div className="flex items-center justify-between">
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                            {modalTitle}
                        </Dialog.Title>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={isSubmitting}
                        >
                            <XMarkIcon className="h-6 w-6" />
                            <span className="sr-only">Fechar</span>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                        <div>
                            <label htmlFor="projectName" className="block text-sm font-medium text-gray-700">
                                Nome do Projeto
                            </label>
                            <input
                                type="text"
                                id="projectName"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="Ex: Novo Website Corporativo"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor={showNewClientForm && !isEditing ? "newClientName" : "projectClient"}
                                    className="block text-sm font-medium text-gray-700"
                                >
                                    {showNewClientForm && !isEditing ? "Nome do Novo Cliente" : "Cliente"}
                                    {isEditing && " (Não pode ser alterado ao editar)"}
                                </label>
                                {!isEditing && (
                                    <button
                                        type="button"
                                        onClick={handleToggleClientForm}
                                        className="p-1 rounded-full text-indigo-600 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                                        title={showNewClientForm ? "Selecionar Cliente Existente" : "Criar Novo Cliente"}
                                        disabled={isSubmitting}
                                    >
                                        {showNewClientForm ? <ArrowUturnLeftIcon className="h-5 w-5" /> : <PlusCircleIcon className="h-5 w-5" />}
                                        <span className="sr-only">{showNewClientForm ? "Selecionar Cliente Existente" : "Criar Novo Cliente"}</span>
                                    </button>
                                )}
                            </div>

                            {showNewClientForm && !isEditing ? (
                                <div>
                                    <input
                                        type="text"
                                        id="newClientName"
                                        value={newClientName}
                                        onChange={(e) => setNewClientName(e.target.value)}
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        placeholder="Ex: Cliente Exemplo Inc."
                                        disabled={isSubmitting}
                                    />
                                </div>
                            ) : (
                                <div>
                                    <select
                                        id="projectClient"
                                        value={clientId}
                                        onChange={(e) => setClientId(e.target.value)}
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        disabled={isSubmitting || loadingClients || isEditing}
                                    >
                                        <option value="" disabled>
                                            {loadingClients ? "Carregando clientes..." :
                                                (isEditing && initialProjectData?.clientId ?
                                                    (clients.find(c => c.id === initialProjectData.clientId)?.name || "Cliente atual (não encontrado na lista)") :
                                                    "Selecione um cliente")}
                                        </option>
                                        {!loadingClients && clients.map((client) => (
                                            <option key={client.id} value={client.id}
                                                disabled={isEditing && client.id !== initialProjectData?.clientId}>
                                                {client.name}
                                            </option>
                                        ))}
                                    </select>
                                    {loadingClients && <p className="text-xs text-gray-500 mt-1">Carregando lista de clientes...</p>}
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="rounded-md bg-red-50 p-3 my-2">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        )}

                        <div className="pt-2 flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn-secondary"
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={isSubmitting || (loadingClients && !showNewClientForm && !isEditing)}
                            >
                                {isSubmitting ? (isEditing ? 'Salvando...' : 'Adicionando...') : submitButtonText}
                            </button>
                        </div>
                    </form>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
} 